import React from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { driverApi } from "../lib/api";

function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("da-DK", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

export default function DriverChatModal({ onClose }) {
  const [messages, setMessages] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [text, setText] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const listRef = React.useRef(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await driverApi().get("/driver/messages");
      setMessages(data.items || []);
      // Mark admin messages as read
      if ((data.unread_count || 0) > 0) {
        await driverApi().post("/driver/messages/mark-all-read");
      }
    } catch (e) {
      toast.error("Kunne ikke hente beskeder");
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    // Poll every 15s while open
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);
  React.useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      const { data } = await driverApi().post("/driver/messages", { text: t });
      setMessages((m) => [...m, data.message]);
      setText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke sende");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-5" data-testid="driver-chat-modal">
      <div className="bg-[#0A0A0A] text-white w-full sm:max-w-md max-h-[92vh] flex flex-col border border-white/10">
        <div className="sticky top-0 bg-[#0A0A0A] border-b border-white/10 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle size={20} className="text-brand-red" />
            <h2 className="font-display font-bold text-lg">Chat med drift</h2>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="chat-close">
            <X size={20} />
          </button>
        </div>

        <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[300px]">
          {loading && messages.length === 0 && (
            <div className="text-center text-white/40 text-sm py-12">
              <Loader2 size={20} className="animate-spin inline mr-2" /> Indlæser…
            </div>
          )}
          {!loading && messages.length === 0 && (
            <div className="text-center text-white/40 text-sm py-12">
              Ingen beskeder endnu — skriv det første ord til drift.
            </div>
          )}
          {messages.map((m) => {
            const fromAdmin = m.direction === "admin_to_driver";
            return (
              <div
                key={m.id}
                className={`flex ${fromAdmin ? "justify-start" : "justify-end"}`}
                data-testid={`chat-msg-${m.id}`}
              >
                <div className={`max-w-[80%] p-3 text-sm ${
                  fromAdmin ? "bg-white/10 text-white" : "bg-brand-red text-white"
                }`}>
                  <div className="text-[10px] uppercase tracking-widest opacity-60 mb-1">
                    {fromAdmin ? "Drift" : "Dig"}
                  </div>
                  <div className="whitespace-pre-wrap break-words">{m.text}</div>
                  <div className="text-[10px] opacity-60 mt-1">{fmtTime(m.created_at)}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="sticky bottom-0 bg-[#0A0A0A] border-t border-white/10 p-3 flex gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={1}
            placeholder="Skriv en besked…"
            className="flex-1 bg-white/5 border border-white/10 text-white p-2 text-sm focus:outline-none focus:border-brand-red resize-none"
            data-testid="chat-input"
          />
          <button
            onClick={send}
            disabled={sending || !text.trim()}
            className="bg-brand-red text-white px-4 hover:bg-red-700 disabled:opacity-60 flex items-center justify-center"
            data-testid="chat-send"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
}
